/**
 * One-time first-run toast shown after the user's first FK-popover "Open"
 * click. Reminds users that "Open" now drills inside the current tab and
 * points to the ⌘-click / ⋯ menu alternatives.
 *
 * Dismisses on click or auto-dismisses after 8s. Emits
 * `feature:fk_nav_first_run_hint_dismissed` with the `via` discriminator.
 */

import { useEffect } from 'react';
import { useAppStore } from '../../../../../store/appStore';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import { toast } from '../../../../../components/ui/toaster';

const HINT_DURATION_MS = 8000;

/**
 * Imperative trigger — call once after the first EP1 "Open" success.
 * Internally checks `appStore.hasSeenFkBreadcrumbHint` and short-circuits
 * if the user has already seen it.
 */
export function maybeShowFkBreadcrumbFirstRunHint(args: {
  readonly track: ReturnType<typeof useAnalytics>['track'];
  readonly hasSeen: boolean;
  readonly markSeen: () => void;
}): void {
  if (args.hasSeen) return;

  args.markSeen();

  let dismissedVia: 'click' | 'auto-timeout' | null = null;
  const reportDismissed = (via: 'click' | 'auto-timeout') => {
    if (dismissedVia) return;
    dismissedVia = via;
    args.track('feature:fk_nav_first_run_hint_dismissed', { via });
  };

  // Slice 3 review: the toaster now accepts a per-toast `testId` override so
  // E2E can assert on a stable selector instead of fuzzy title-text matches.
  toast.default({
    title: 'New: Open drills in the current tab',
    description: '⌘-click or use the ⋯ menu to open in a new tab.',
    duration: HINT_DURATION_MS,
    testId: 'fk-breadcrumb-first-run-hint',
    action: {
      label: 'Got it',
      onClick: () => reportDismissed('click'),
    },
  });

  // Fall-back timer in case the toast auto-dismisses without an action click.
  // (The toaster removes the toast on timer expiry, but doesn't notify; we
  // report `auto-timeout` after the same delay.)
  if (typeof window !== 'undefined') {
    window.setTimeout(() => reportDismissed('auto-timeout'), HINT_DURATION_MS + 50);
  }
}

/**
 * Convenience React hook for one-shot triggering. Returns an imperative
 * callback the caller can fire on the EP1 "Open" success path.
 */
export function useFkBreadcrumbFirstRunHint(): () => void {
  const { track } = useAnalytics();
  const hasSeen = useAppStore((s) => s.hasSeenFkBreadcrumbHint);
  const setHasSeen = useAppStore((s) => s.setHasSeenFkBreadcrumbHint);

  // No-op side-effect — included so the hook participates in the React tree
  // and re-reads `hasSeen` from the persisted store on remount.
  useEffect(() => undefined, []);

  return () => {
    maybeShowFkBreadcrumbFirstRunHint({
      track,
      hasSeen,
      markSeen: () => setHasSeen(true),
    });
  };
}

// Test-only hook: expose the hint trigger to E2E so tests can drive the
// toast surface without seeding a full FK metadata pipeline through the
// mock engine. Only attached when the test-mode flag is set; never
// exposed in production builds.
if (typeof window !== 'undefined' && window.electronAPI?.testMode?.isTestMode?.()) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__SQL_STUDIO_TRIGGER_FK_HINT__ = () => {
    // Re-read the latest store values at call time so the trigger respects
    // the test's most-recent setState.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const get = (window as any).__SQL_STUDIO_APP_STORE_GET__;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const set = (window as any).__SQL_STUDIO_APP_STORE_SET__;
    maybeShowFkBreadcrumbFirstRunHint({
      track: (name, props) =>
        window.electronAPI?.analytics?.track(name, props ?? {}).catch(() => {}),
      hasSeen: !!get?.()?.hasSeenFkBreadcrumbHint,
      markSeen: () => set?.({ hasSeenFkBreadcrumbHint: true }),
    });
  };
}
